export interface AnalyticsPlugin {
  name: string;
  initialize?: () => void | Promise<void>;
  identify?: (userId: string, traits?: Record<string, any>) => void;
  track?: (eventName: string, properties?: Record<string, any>) => void;
  screen?: (screenName: string, properties?: Record<string, any>) => void;
  reset?: () => void;
}

class ConsoleLoggerPlugin implements AnalyticsPlugin {
  name = 'ConsoleLogger';

  identify(userId: string, traits?: Record<string, any>) {
    console.log(`[Analytics:Identify] User: ${userId}`, traits || {});
  }

  track(eventName: string, properties?: Record<string, any>) {
    console.log(`[Analytics:Track] Event: ${eventName}`, properties || {});
  }

  screen(screenName: string, properties?: Record<string, any>) {
    console.log(`[Analytics:Screen] Screen: ${screenName}`, properties || {});
  }

  reset() {
    console.log(`[Analytics:Reset] Session reset`);
  }
}

class AnalyticsService {
  private plugins: AnalyticsPlugin[] = [];
  private isInitialized = false;

  constructor() {
    // Default to including a console logger in development
    if (process.env.NODE_ENV === 'development') {
      this.registerPlugin(new ConsoleLoggerPlugin());
    }
  }

  registerPlugin(plugin: AnalyticsPlugin) {
    if (!this.plugins.some((p) => p.name === plugin.name)) {
      this.plugins.push(plugin);
      if (this.isInitialized && plugin.initialize) {
        plugin.initialize();
      }
    }
  }

  async initialize() {
    if (this.isInitialized) return;

    for (const plugin of this.plugins) {
      if (plugin.initialize) {
        await plugin.initialize();
      }
    }
    this.isInitialized = true;
  }

  identify(userId: string, traits?: Record<string, any>) {
    this.plugins.forEach((plugin) => plugin.identify?.(userId, traits));
  }

  track(eventName: string, properties?: Record<string, any>) {
    this.plugins.forEach((plugin) => plugin.track?.(eventName, properties));
  }

  screen(screenName: string, properties?: Record<string, any>) {
    this.plugins.forEach((plugin) => plugin.screen?.(screenName, properties));
  }

  reset() {
    this.plugins.forEach((plugin) => plugin.reset?.());
  }
}

// Export a singleton instance
export const Analytics = new AnalyticsService();
